import { Dimensions, Platform } from 'react-native';
import {
  MapMarkerProps,
  default as MapView,
  MapViewProps,
} from 'react-native-maps';
import React, { useEffect, useMemo } from 'react';

import { Location } from '@lib/types';
import { StyleObj } from '@providers/Theme/types';
import { crashlyticsUtil } from '@lib/crashlytics';
import { makeStyles } from '@providers/Theme';

type MapProps = Omit<MapViewProps, 'region'> & {
  style?: StyleObj;
  location: Location;
};

const useStyles = makeStyles({
  root: {
    width: '100%',
    height: 300,
  },
});

const Marker: React.FC<MapMarkerProps> =
  Platform.OS === 'web'
    ? (MapView as any).Marker
    : require('react-native-maps').Marker;

export const Map: React.FC<MapProps> = (props) => {
  const styles = useStyles();

  const latitude = Number(props.location?.lat);
  const longitude = Number(props.location?.lon);

  const delta = useMemo(() => {
    const { height, width } = Dimensions.get('window');

    const latitudeDelta = 0.008;
    const longitudeDelta = latitudeDelta * (width / height);

    return { latitudeDelta, longitudeDelta };
  }, []);

  const isValidCoords =
    Number.isFinite(latitude) &&
    Number.isFinite(longitude) &&
    latitude >= -90 &&
    latitude <= 90 &&
    longitude >= -180 &&
    longitude <= 180;

  useEffect(() => {
    crashlyticsUtil.log(
      `Map render | lat=${latitude} lon=${longitude} deltaLat=${delta.latitudeDelta} deltaLon=${delta.longitudeDelta}`
    );

    if (!isValidCoords) {
      crashlyticsUtil.recordError(
        new Error(
          `Invalid Map coordinates lat=${props.location?.lat} lon=${props.location?.lon}`
        )
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [latitude, longitude]);

  if (!isValidCoords) {
    return null;
  }

  const region = {
    latitude,
    longitude,
    ...delta,
  };

  return (
    <MapView
      loadingEnabled={true}
      moveOnMarkerPress={false}
      pitchEnabled={true}
      scrollEnabled={true}
      showsCompass={true}
      showsMyLocationButton={true}
      showsPointsOfInterest={false}
      showsScale={true}
      zoomControlEnabled={true}
      zoomEnabled={true}
      toolbarEnabled={false}
      region={region}
      style={[styles.root, props.style]}
      {...props}
    >
      <Marker
        coordinate={{
          latitude,
          longitude,
        }}
      />
    </MapView>
  );
};
